import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { Row, Col, Button } from 'reactstrap';
import Sidebar from '../../Sidebar';
import { useNavigate, useParams } from 'react-router';
import { useCookies } from 'react-cookie';
import FloatingControls from '../../components/FloatingControls';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import ErrorToast from '../../ErrorToast';
import { displayError } from '../../helpers';
import '../../button.css';
import { getUserAllowedPlants } from '../../services/PlantServices';
import TableRowsDieselConsumption from './TableRowsDieselConsumption';
import {
    deleteDieselConsumption,
    getDieselConsumptionDetails,
} from '../../services/DieselConsumptionServices';
import { printDieselConsumption } from '../../utils/dieselConsumptionPrint';

function DieselConsumptionView() {
    const { id } = useParams();
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [plants, setPlants] = useState([]);
    const [rowsData, setRowsData] = useState([]);
    const [inProgress, setInProgress] = useState(true);
    const [isActive, setIsActive] = useState(true);
    const [inputs, setInputs] = useState({});

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || []).filter((p) => p.status).map((p) => ({
                value: p.id,
                label: p.plant_alias,
            })));
        });
    }, [cookies]);

    useEffect(() => {
        if (!id) return;
        setInProgress(true);
        getDieselConsumptionDetails(cookies, id)
            .then((record) => {
                const active = record.status === true || record.status === 1 || record.status === 'Active';
                setIsActive(active);
                setInputs({
                    plant_id: record.plant_id,
                    consumption_date: record.consumption_date,
                    consumption_time: record.consumption_time?.slice(0, 5) || '',
                    consumption_no: record.sl_no,
                    prefix: record.prefix,
                    issued_by: record.issued_by || '',
                    remarks: record.remarks || '',
                    status_label: active ? 'Active' : 'Cancelled',
                });
                setRowsData(record.detail_list || []);
            })
            .catch((error) => displayError(error?.response?.data || error, 'Load Failed'))
            .finally(() => setInProgress(false));
    }, [cookies, id]);

    const totalQty = useMemo(
        () => rowsData.reduce((sum, row) => sum + (Number(row.consumed_qty) || 0), 0),
        [rowsData],
    );

    const plantLabel = plants.find((p) => p.value === inputs.plant_id)?.label || '';

    const handleDelete = () => {
        Swal.fire({
            title: 'Delete diesel consumption?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
        }).then((result) => {
            if (!result.isConfirmed) return;
            setInProgress(true);
            deleteDieselConsumption(cookies, id)
                .then(() => {
                    Swal.fire('Deleted', 'Record deleted.', 'success');
                    navigate('/DieselConsumptionTable');
                })
                .catch((error) => displayError(error?.response?.data || error, 'Delete Failed'))
                .finally(() => setInProgress(false));
        });
    };

    const handlePrint = () => {
        printDieselConsumption(cookies, id).catch((error) => {
            displayError(error?.response?.data || error, 'Print Failed');
        });
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <ErrorToast />
            <LoadingOverlay active={inProgress} />
            <div id="page-wrap">
                <FloatingControls
                    tableLink="/DieselConsumptionTable"
                    formLink="/DieselConsumption"
                    addNewLabel="Main Form"
                    showSave={false}
                    enablePrint
                    onPrint={handlePrint}
                    enableCancel
                    onCancel={() => navigate('/DieselConsumptionTable')}
                    enableDelete={!isActive}
                    onDelete={handleDelete}
                />

                <FormPageLayout
                    moduleLabel="STORE"
                    title="Diesel Consumption"
                    subtitle="Read-only view of the diesel consumption voucher and line details."
                    modeLabel="VIEW MODE"
                >
                <div className="container form-control-panel">
                    <Row>
                        <Col xl={6} lg={12} md={12}>
                            <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                <label className="form-group col-sm-5 text-right">Plant</label>
                                <input className="form-control col-sm-6" value={plantLabel} readOnly /><br />

                                <label className="form-group col-sm-5 text-right">Date</label>
                                <input className="form-control col-sm-6" value={inputs.consumption_date || ''} readOnly /><br />

                                <label className="form-group col-sm-5 text-right">Time</label>
                                <input className="form-control col-sm-6" value={inputs.consumption_time || ''} readOnly /><br />

                                <label className="form-group col-sm-5 text-right">Consumption No</label>
                                <input className="form-control col-sm-6" value={`${inputs.prefix || ''}/${inputs.consumption_no || ''}`} readOnly /><br />

                                <label className="form-group col-sm-5 text-right">Issued By</label>
                                <input className="form-control col-sm-6" value={inputs.issued_by || ''} readOnly /><br />

                                <label className="form-group col-sm-5 text-right">Status</label>
                                <input className="form-control col-sm-6" value={inputs.status_label || ''} readOnly /><br />

                                <label className="form-group col-sm-5 text-right">Remarks</label>
                                <textarea className="form-control col-sm-6" rows="2" value={inputs.remarks || ''} readOnly /><br />
                            </div>
                        </Col>
                        <Col xl={6} lg={12} md={12}>
                            <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                <label className="form-group col-sm-5 text-right">Total Diesel Qty</label>
                                <input className="form-control col-sm-6" value={totalQty.toFixed(3)} readOnly style={{ textAlign: 'right', fontWeight: 'bold' }} /><br />
                            </div>
                        </Col>
                    </Row>

                    <div className="table-responsive shadow p-2 my-2 border-secondary">
                        <h4><b>Diesel Consumption Lines</b></h4>
                        <table className="table table-bordered table-sm">
                            <thead className="thead-light text-center">
                                <tr>
                                    <th>#</th>
                                    <th>Equipment / Vehicle</th>
                                    <th>Driver</th>
                                    <th>Purpose</th>
                                    <th>Diesel Product</th>
                                    <th>Opening Meter</th>
                                    <th>Closing Meter</th>
                                    <th>Consumed Qty</th>
                                    <th>Unit</th>
                                </tr>
                            </thead>
                            <tbody>
                                <TableRowsDieselConsumption
                                    rowsData={rowsData}
                                    equipments={[]}
                                    dieselProducts={[]}
                                    onChange={() => {}}
                                    onRemove={() => {}}
                                    readOnly
                                />
                            </tbody>
                        </table>
                    </div>

                    <div className="footer company-form-footer text-center">
                        {isActive && (
                            <Button type="button" className="btn btn-twitter company-form-action-btn" style={{ width: '80px', fontWeight: 'bold' }} onClick={() => navigate(`/DieselConsumptionEdit/${id}`)}>
                                <i className="fa fa-pencil" aria-hidden="true" /> Edit
                            </Button>
                        )}
                        <Button type="button" className="btn btn-twitter company-form-action-btn" style={{ width: '80px', fontWeight: 'bold' }} onClick={handlePrint}><i className="fa fa-print" aria-hidden="true" /> Print</Button>
                        {!isActive && (
                            <Button type="button" className="btn btn-twitter company-form-action-btn" style={{ width: '80px', fontWeight: 'bold' }} onClick={handleDelete}><i className="fa fa-trash" aria-hidden="true" /> Delete</Button>
                        )}
                        <Button type="button" className="btn btn-twitter company-form-action-btn" style={{ width: '110px', fontWeight: 'bold' }} onClick={() => navigate('/DieselConsumption')}><i className="fa fa-file-text-o" aria-hidden="true" /> Main Form</Button>
                        <Button type="button" className="btn btn-twitter company-form-action-btn" style={{ width: '110px', fontWeight: 'bold' }} onClick={() => navigate('/DieselConsumptionTable')}><i className="fa fa-eye" aria-hidden="true" /> View</Button>
                        <Button type="button" className="btn btn-twitter company-form-action-btn" style={{ width: '80px', fontWeight: 'bold' }} onClick={() => navigate('/Home')}><i className="fa fa-home" aria-hidden="true" /> Home</Button>
                    </div>
                </div>
                </FormPageLayout>
            </div>
        </div>
    );
}

export default DieselConsumptionView;
